import React from 'react';
import {Box, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import {useHistory} from 'react-router-dom';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import NetworkDashboardShell from '../components/NetworkDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, ORANGE, PageHeader, StatCard, ScoreBar, BandChip, headCell, bodyCell, MARKER_SECONDARY, readinessBandColor, riskBandColor, contextBandColor} from '../components/widgets.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, whole, benchmarkOf, addInfoOf, titleCase, orgColumn} from '../networkData';
import {isSuppressedKpi, isSuppressedItem, kpiNote} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';
import {READINESS_BAND_COLORS} from '../../../utils/constants';

import {UNIT, fmtValue} from '../../DashboardCommon/units';
/* Slide 13 — Network Executive Overview (SND-13-01 … SND-13-09).

   Every figure comes from the network's own dashboard, read by card code as
   the P1 Dashboard Data Map (Boxes sheet) maps each box:

     SND-13-01  header strip             SND.S_3.C_2   ORGCard
     SND-13-02  Readiness Composite      SND.S_3.C_3   KPICard (+ Global avg.)
     SND-13-03  Any Cyber Risk           SND.S_3.C_4   KPICard (+ Global avg.)
     SND-13-04  Participation            SND.S_3.C_5   KPICard (participants / registered)
     SND-13-05  Schools Assessed         SND.S_9.C_4   KPICard — from the Participation
                                                        section, not the overview
     SND-13-06  Top Readiness Areas      SND.S_3.C_6   BarChartBMCard
     SND-13-07  Top Context & Support    SND.S_3.C_6a  BarChartBMCard
     SND-13-08  Top Child Cyber-Risks    SND.S_3.C_7   BarChartCard
     SND-13-09  Schools to Watch         SND.S_4.C_7   BarChartCard ("Lowest School
                                                        Composite" — readiness scores)

   Only the Global avg marker is drawn, per the deck's "Comparison w/global
   avg." note on this slide — the cards also carry a National avg., unused here.
   A card that is missing leaves its section empty rather than a stand-in figure. */

// The header strip shows these four, in this order (SND-13-01). The card also
// carries COUNTRY, which this slide does not show.
const INFO_COLUMNS = ['NETWORK', 'ASSESSMENT WINDOW', 'REPORT DATE', 'SOURCE'];

/*
 * The card's items, in its own order, capped at `limit`. Items whose label is
 * already shown are skipped first: the risk card carries "Any Cyber Risk" twice
 * (codes expe_CPCI_CyberRisks and expe_CyberRisks), which would otherwise take
 * two of the five slots and repeat a row.
 */
const toBars = (card, limit) => {
    const seen = new Set();
    return (card?.items || [])
        .filter((item) => {
            const label = String(item.label || '').trim();
            if (seen.has(label)) return false;
            seen.add(label);
            return true;
        })
        .slice(0, limit)
        .map((item) => ({
            label: item.label,
            // A group under the n floor arrives flagged (a ranked list normally leaves it out): no score, band or bar.
            suppressed: isSuppressedItem(item),
            score: isSuppressedItem(item) ? null : whole(item.value),
            band: isSuppressedItem(item) ? null : item.band,
            global: benchmarkOf(item, 'Global avg.'),
        }));
};

// `linkTo` makes the title a clickable "→" link to another Baseline Results
// page — e.g. "Top Child Cyber Risks" → the Cyber Risks page, per the deck's
// own "→ Cyber Risks" annotation on this slide. `headerRight` (e.g. the
// Global avg legend) sits inline at the right end of the title row.
const Panel = ({title, linkTo, headerRight, children}) => {
    const history = useHistory();
    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
        }}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, gap: 1.5}}>
                {linkTo ? (
                    <Box
                        onClick={() => history.push(linkTo)}
                        sx={{display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', width: 'fit-content', '&:hover': {textDecoration: 'underline'}}}
                    >
                        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{title}</Typography>
                        <ArrowForwardIcon style={{fontSize: 18, color: ORANGE}}/>
                    </Box>
                ) : (
                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{title}</Typography>
                )}
                {headerRight}
            </Box>
            {children}
        </Box>
    );
};

const GlobalAvgLegend = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0}}>
        <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
        <Typography sx={{fontSize: '11px', color: '#6A7282'}}>Global avg</Typography>
    </Box>
);

// "Schools to Watch" (SND-13-09) — every column sorts from its header, and filters once the
// network has enough schools (QA 9 Oct 2026). Its own component so the hook runs below the
// page's loading branches. A suppressed school has no score or level to sort by.
const WATCH_COLUMNS = [
    {key: 'school', label: 'School', type: 'text'},
    // SND.S_4.C_7 carries each school's readiness composite, not a risk rate.
    {key: 'score', label: 'Score', type: 'number', accessor: (s) => (s.suppressed ? null : s.score)},
    {key: 'level', label: 'Level', type: 'band', order: Object.keys(READINESS_BAND_COLORS), accessor: (s) => (s.suppressed ? null : s.level)},
];
const SchoolsToWatchTable = ({rows, emptyText}) => {
    const t = useTableControls(rows, WATCH_COLUMNS);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('school')} sx={{...headCell, py: 1.25}}/>
                            <SortFilterHeaderCell {...t.headerProps('score')} sx={{...headCell, py: 1.25, textAlign: 'center'}}/>
                            <SortFilterHeaderCell {...t.headerProps('level')} sx={{...headCell, py: 1.25}}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {emptyText && (
                            <TableRow>
                                <TableCell colSpan={3} sx={{...bodyCell, textAlign: 'center', color: '#6A7282', py: 3}}>{emptyText}</TableCell>
                            </TableRow>
                        )}
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} sx={{...bodyCell, textAlign: 'center', color: '#6A7282', py: 3}}>No school matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((s) => (
                            <TableRow key={s.school}>
                                <TableCell sx={{...bodyCell, fontWeight: 700, color: NAVY}}>{s.school}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700}}>{s.suppressed ? <SuppressedValue variant="cell"/> : (s.score ?? '—')}</TableCell>
                                <TableCell sx={bodyCell}>{s.level && <BandChip label={s.level} color={readinessBandColor(s.level)}/>}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const BASELINE_OVERVIEW_SECTION_CODE = 'S_3';
const SCHOOL_SCORES_SECTION_CODE = 'S_4';
const PARTICIPATION_SECTION_CODE = 'S_9';

const CARD_SECTIONS = [
    BASELINE_OVERVIEW_SECTION_CODE,
    SCHOOL_SCORES_SECTION_CODE,
    PARTICIPATION_SECTION_CODE,
];

const NetworkSummaryPage = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    const orgCard = findCard(dashboardData, BASELINE_OVERVIEW_SECTION_CODE, 'C_2');
    const INFO_STRIP = INFO_COLUMNS
        .map((col) => ({label: titleCase(col), value: orgColumn(orgCard, col)}))
        .filter((i) => i.value !== null);

    const composite = findCard(dashboardData, BASELINE_OVERVIEW_SECTION_CODE, 'C_3');
    const anyRisk = findCard(dashboardData, BASELINE_OVERVIEW_SECTION_CODE, 'C_4');
    const participation = findCard(dashboardData, BASELINE_OVERVIEW_SECTION_CODE, 'C_5');
    const schoolsAssessed = findCard(dashboardData, PARTICIPATION_SECTION_CODE, 'C_4');

    // The benchmark line under a KPI, in the KPI's own unit ("Global avg 54" for the composite, "Global avg 76%" for risk).
    const globalLine = (card, unit) => {
        const g = addInfoOf(card, 'Global avg.');
        return g === null ? undefined : `Global avg ${fmtValue(g, unit)}`;
    };
    const participants = addInfoOf(participation, 'Participants');
    const registered = addInfoOf(participation, 'Registered');
    // A KPI under the n floor (suppressed) reads "—" with the reason underneath; a value that is not sent reads "—".
    const kpiValue = (card, pct) => {
        if (isSuppressedKpi(card)) return '—';
        const v = whole(card.kpi_value);
        return v === null ? '—' : (pct ? `${v}%` : String(v));
    };
    const scoreKpi = (card, label, pct, colorOf) => card && {
        label,
        value: kpiValue(card, pct),
        band: isSuppressedKpi(card) ? undefined : card.kpi_band,
        bandColorOverride: isSuppressedKpi(card) ? undefined : colorOf(card.kpi_band),
        sub: isSuppressedKpi(card) ? kpiNote(card) : globalLine(card, pct ? UNIT.PERCENT : UNIT.SCORE),
    };

    const KPI_CARDS = [
        scoreKpi(composite, 'Readiness Composite Score', false, readinessBandColor),
        scoreKpi(anyRisk, 'Any Cyber Risk', true, riskBandColor),
        participation && {
            label: 'Participation',
            value: kpiValue(participation, true),
            band: participation.kpi_band,
            sub: participants !== null && registered !== null
                ? `${whole(participants).toLocaleString()} / ${whole(registered).toLocaleString()}`
                : undefined,
        },
        schoolsAssessed && {
            label: 'Schools Assessed',
            value: kpiValue(schoolsAssessed, false),
        },
    ].filter(Boolean);

    // Top three readiness areas, top two context areas, top five risks — the
    // counts the slide draws.
    const TOP_READINESS_AREAS = toBars(findCard(dashboardData, BASELINE_OVERVIEW_SECTION_CODE, 'C_6'), 3);
    const TOP_CONTEXT_SCORES = toBars(findCard(dashboardData, BASELINE_OVERVIEW_SECTION_CODE, 'C_6a'), 2);
    const TOP_CYBER_RISKS = toBars(findCard(dashboardData, BASELINE_OVERVIEW_SECTION_CODE, 'C_7'), 5)
        .map((r) => ({label: r.label, pct: r.score, band: r.band, global: r.global, suppressed: r.suppressed}));

    // "Lowest School Composite" — each school's readiness composite, in the
    // order the datahouse gives. A score, so no percent sign.
    const schoolsToWatchCard = findCard(dashboardData, SCHOOL_SCORES_SECTION_CODE, 'C_7');
    const SCHOOLS_TO_WATCH = (schoolsToWatchCard?.items || []).map((item) => ({
        school: item.label,
        suppressed: isSuppressedItem(item),
        score: isSuppressedItem(item) ? null : whole(item.value),
        level: isSuppressedItem(item) ? null : item.band,
    }));
    // The datahouse leaves schools under the n floor out of this ranking; a card with no row left says so.
    const SCHOOLS_TO_WATCH_EMPTY = schoolsToWatchCard && SCHOOLS_TO_WATCH.length === 0
        ? 'No schools with 10 or more students to rank.'
        : null;

    if (loading) {
        return (
            <NetworkDashboardShell needsAssessmentResults>
                <CircularLoader />
            </NetworkDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <NetworkDashboardShell needsAssessmentResults>
                <NoDataAvailable />
            </NetworkDashboardShell>
        );
    }

    return (
    <NetworkDashboardShell needsAssessmentResults>
        <PageHeader
            title="Network Baseline Overview"
            subtitle="How is the school network doing overall?"
        />

        <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: {xs: 2, md: 0},
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', mb: 3, overflow: 'hidden',
        }}>
            {INFO_STRIP.map((i, idx) => (
                <Box key={i.label} sx={{
                    flex: {xs: '1 1 40%', md: 1}, px: 2.5, py: 2,
                    borderRight: {md: idx < INFO_STRIP.length - 1 ? '1px solid #E5E7EB' : 'none'},
                }}>
                    <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5}}>
                        {i.label}
                    </Typography>
                    <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>{i.value}</Typography>
                </Box>
            ))}
        </Box>

        <Grid container spacing={2.5} sx={{mb: 3}}>
            {KPI_CARDS.map((kpi) => (
                <Grid item xs={12} sm={6} md={3} key={kpi.label}>
                    <StatCard {...kpi}/>
                </Grid>
            ))}
        </Grid>

        <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
                <Panel title="Top Readiness Area Scores" headerRight={<GlobalAvgLegend/>}>
                    {TOP_READINESS_AREAS.map((a) => (
                        <ScoreBar key={a.label} label={a.label} score={a.score} band={a.band} global={a.global} bandColorOverride={readinessBandColor(a.band)} suppressed={a.suppressed}/>
                    ))}

                    <Box sx={{mt: 3, pt: 2.5, borderTop: '1px solid var(--brand-border, #E5E7EB)'}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, gap: 1.5}}>
                            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                                Top Context & Support Scores
                            </Typography>
                            <GlobalAvgLegend/>
                        </Box>
                        {TOP_CONTEXT_SCORES.map((a) => (
                            <ScoreBar key={a.label} label={a.label} score={a.score} band={a.band} global={a.global} bandColorOverride={contextBandColor(a.band)} suppressed={a.suppressed}/>
                        ))}
                    </Box>
                </Panel>
            </Grid>

            <Grid item xs={12} md={4}>
                <Panel title="Top Child Cyber Risks" headerRight={<GlobalAvgLegend/>}>
                    {TOP_CYBER_RISKS.map((r) => (
                        <ScoreBar key={r.label} label={r.label} score={r.pct} band={r.band} global={r.global} bandColorOverride={riskBandColor(r.band)} suppressed={r.suppressed} unit="percent"/>
                    ))}
                </Panel>
            </Grid>

            <Grid item xs={12} md={4}>
                <Panel title="Schools to Watch">
                    <SchoolsToWatchTable rows={SCHOOLS_TO_WATCH} emptyText={SCHOOLS_TO_WATCH_EMPTY}/>
                </Panel>
            </Grid>
        </Grid>
    </NetworkDashboardShell>
    );
};

export default NetworkSummaryPage;
